// Thin fetch wrapper for /ops.
//
// Responsibilities:
//   - Prefix absolute paths with the current origin (requests stay on-site).
//   - Inject the CSRF double-submit header (value comes from the ops_csrf
//     cookie, which the server sets after login).
//   - Surface 401 as a typed error so callers can redirect to /ops/login
//     without depending on global fetch interceptors.

// The two apps share this module and are told apart by the path they are
// served from, so there is one implementation rather than two that drift.
const ON_ADMIN =
  typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
const LOGIN_PATH = ON_ADMIN ? "/admin/login" : "/ops/login";

export class ApiError extends Error {
  status: number;
  code: string;
  /** The parsed error body, when the server sent one.
   *
   *  Some failures carry structure worth rendering rather than flattening into
   *  a toast — a campaign that cannot be activated returns the LIST of reasons,
   *  and "cannot activate" on its own teaches nobody what to fix. Callers that
   *  do not need it simply ignore it. */
  payload: unknown;
  constructor(status: number, code: string, message?: string, payload?: unknown) {
    super(message ?? code);
    this.status = status;
    this.code = code;
    this.payload = payload ?? null;
  }
}

/**
 * The CSRF token, under either name.
 *
 * The server sets __Host-ops_csrf in production and the bare ops_csrf in
 * development, because the __Host- prefix requires Secure and dev is plain
 * HTTP. Reading only one name would break every write on one of the two.
 * The prefixed name wins: during the hour after a deploy a browser can still
 * be holding the old bare cookie from the previous session.
 */
export function readCsrfCookie(): string | null {
  return readCookie("__Host-ops_csrf") ?? readCookie("ops_csrf");
}

function readCookie(name: string): string | null {
  const match = document.cookie
    .split(";")
    .map((p) => p.trim())
    .find((p) => p.startsWith(`${name}=`));
  if (!match) return null;
  const value = match.slice(name.length + 1);
  try {
    return decodeURIComponent(value);
  } catch {
    return null;
  }
}

interface OpsFetchInit extends Omit<RequestInit, "body"> {
  json?: unknown;
  /** A file sent as-is, e.g. an image upload. Set its Content-Type in `headers`. */
  blob?: Blob;
}

// Tenant switcher state. The currently-selected tenant slug is persisted
// in localStorage so reloads preserve the scope. Every outgoing request
// injects it as X-Chloe-Tenant-Slug, which the server's
// resolveTenantFromOpsSession middleware consumes. Owner ops users may
// leave this unset to see the platform-wide (cross-tenant) view.
const ACTIVE_TENANT_STORAGE_KEY = "chloe-ops:active-tenant-slug";

// "All workspaces" is a CHOICE, and the absence of the key is a different
// thing: nobody has picked a scope on this browser yet. They used to be the
// same (the key was simply removed), so there was no way to tell a platform
// owner who wants the cross-tenant view from one who has just signed in — and
// a fresh sign-in landed on Home with nothing to show but "pick a tenant".
// App.tsx now defaults the scope to the first workspace when no choice has
// been made; picking "All workspaces" writes this sentinel and sticks.
const ALL_WORKSPACES = "__all__";

export function getActiveTenantSlug(): string | null {
  try {
    const v = localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY);
    return v && v !== ALL_WORKSPACES ? v : null;
  } catch {
    return null;
  }
}

/** True once a scope has been settled on this browser — a workspace or an
 *  explicit "All workspaces". False on a fresh sign-in (logout clears it). */
export function hasTenantScopeChoice(): boolean {
  try {
    return localStorage.getItem(ACTIVE_TENANT_STORAGE_KEY) != null;
  } catch {
    return false;
  }
}

/** Forget the scope entirely, so the next sign-in gets the default again.
 *  Logout only — everywhere else, null means "All workspaces, deliberately". */
export function clearTenantScopeChoice(): void {
  try {
    localStorage.removeItem(ACTIVE_TENANT_STORAGE_KEY);
    localStorage.removeItem(ACTIVE_AGENT_STORAGE_KEY);
  } catch {
    /* ignore localStorage failures */
  }
  window.dispatchEvent(new Event("chloe-ops:tenant-changed"));
}

// Append the active tenant slug as a `?t=` query param. Use for browser-native
// requests (<audio>/<video> src, download links, direct navigation) that can't
// send the X-Chloe-Tenant-Slug header. The server enforces access regardless.
export function tenantScopedUrl(path: string): string {
  const slug = getActiveTenantSlug();
  if (!slug) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}t=${encodeURIComponent(slug)}`;
}

export function setActiveTenantSlug(slug: string | null): void {
  try {
    localStorage.setItem(ACTIVE_TENANT_STORAGE_KEY, slug ?? ALL_WORKSPACES);
    // An agent belongs to one workspace; a stale id from the previous
    // workspace would just be ignored server-side, but clear it anyway.
    localStorage.removeItem(ACTIVE_AGENT_STORAGE_KEY);
  } catch {
    /* ignore localStorage failures */
  }
  // Let every mounted component refresh its queries.
  window.dispatchEvent(new Event("chloe-ops:tenant-changed"));
}

// The agent the Studio is looking at, sent as X-Chloe-Agent-Id so every
// persona / tools / cost endpoint answers for THAT agent. The server validates
// it belongs to the active workspace (ops/agent-scope.ts); an id from anywhere
// else is ignored, never honoured.
const ACTIVE_AGENT_STORAGE_KEY = "chloe-ops:active-agent-id";

export function getActiveAgentId(): string | null {
  try {
    return localStorage.getItem(ACTIVE_AGENT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setActiveAgentId(id: string | null): void {
  try {
    if (id) localStorage.setItem(ACTIVE_AGENT_STORAGE_KEY, id);
    else localStorage.removeItem(ACTIVE_AGENT_STORAGE_KEY);
  } catch {
    /* ignore localStorage failures */
  }
  window.dispatchEvent(new Event("chloe-ops:agent-changed"));
}

// Impersonation removed Apr 2026 — log in as the actual user instead.
// Stale localStorage entries are ignored; nothing reads them.

// Concurrent identical GETs share one request. React 18's StrictMode mounts
// every effect twice in development, so each dashboard fired its (expensive)
// aggregate query twice per load and the server answered both; two sibling
// components asking for the same resource do the same in production. Keyed by
// path + active tenant/agent, cleared as soon as the response settles.
const inflightGets = new Map<string, Promise<unknown>>();

export function opsFetch<T = unknown>(
  path: string,
  init: OpsFetchInit = {},
): Promise<T> {
  const method = (init.method ?? "GET").toUpperCase();
  if (method !== "GET") return opsFetchRaw<T>(path, init);
  const key = `${getActiveTenantSlug() ?? ""}|${getActiveAgentId() ?? ""}|${path}`;
  const hit = inflightGets.get(key);
  if (hit) return hit as Promise<T>;
  const p = opsFetchRaw<T>(path, init).finally(() => {
    if (inflightGets.get(key) === p) inflightGets.delete(key);
  });
  inflightGets.set(key, p);
  return p;
}

async function opsFetchRaw<T = unknown>(
  path: string,
  init: OpsFetchInit = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");

  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
  }

  const csrf = readCsrfCookie();
  const unsafe = !["GET", "HEAD", "OPTIONS"].includes((init.method ?? "GET").toUpperCase());
  if (unsafe && csrf) {
    headers.set("X-Ops-CSRF", csrf);
  }

  // /admin is cross-tenant. It shares this module, this origin and therefore
  // the active-workspace slug in localStorage, so without this the console
  // would silently answer for whatever workspace /ops was last looking at.
  // Its per-workspace reads take the id in the PATH (/ops/api/tenants/:id),
  // never the header.
  const tenant = ON_ADMIN ? null : getActiveTenantSlug();
  if (tenant) {
    headers.set("X-Chloe-Tenant-Slug", tenant);
  }
  const agent = getActiveAgentId();
  if (agent) {
    headers.set("X-Chloe-Agent-Id", agent);
  }

  const res = await fetch(path, {
    ...init,
    headers,
    body: init.json !== undefined ? JSON.stringify(init.json) : init.blob,
    credentials: "same-origin",
    // Authed, live ops data (polled inbox + open thread): bypass the HTTP cache
    // so every request returns a fresh 200 body. Without this the browser fires
    // conditional GETs that come back 304, which this helper treats as non-ok.
    cache: "no-store",
  });

  // Session expired mid-session → all authed endpoints 401. Rather than
  // let every page handle this, redirect once, globally. Exception:
  // /auth/login and /auth/totp return 401 as their "invalid credentials"
  // response — callers handle those locally, so we skip the redirect.
  if (res.status === 401 && !path.startsWith("/ops/auth/")) {
    if (!window.location.pathname.startsWith(LOGIN_PATH)) {
      window.location.href = LOGIN_PATH;
      // The navigation takes effect asynchronously; throw so the caller's
      // `.then` chain doesn't try to use a non-existent response.
      throw new ApiError(401, "unauthenticated");
    }
  }

  const contentType = res.headers.get("Content-Type") ?? "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    const code = (payload && typeof payload === "object" && "error" in payload
      ? String((payload as { error: unknown }).error)
      : res.statusText) || "unknown_error";
    // WHAT A HUMAN SHOULD READ, not what a machine matches on.
    //
    // The server's convention is `error` — a machine code — plus, wherever
    // somebody took the trouble to write one, `hint`: a full sentence naming
    // what happened and who fixes it. This only ever looked at `message`, which
    // almost nothing sends, so ApiError fell through to `super(code)` and every
    // `setError(e.message)` and every toast in the app rendered the CODE while
    // discarding the sentence. A supervisor clicking "Connect my phone" was
    // shown `no_extension`; the server had said "No extension is assigned to
    // you, so you will not receive calls. A tenant admin assigns one on the
    // Softphone agents page." There are nine such hints in server/.
    //
    // `code` is untouched and is still the thing to branch on. A caller that
    // needs to KNOW which failure this is reads `e.code`; regex-matching the
    // message for a code was always fragile and is now wrong.
    const p = payload && typeof payload === "object" ? (payload as Record<string, unknown>) : null;
    const message =
      p && typeof p.message === "string" && p.message.trim()
        ? p.message
        : p && typeof p.hint === "string" && p.hint.trim()
          ? p.hint
          : undefined;
    throw new ApiError(res.status, code, message, payload);
  }

  return (payload ?? ({} as T)) as T;
}
